import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Optional: neat session label
function SessionLabel({ sessionName }) {
  return sessionName ? (
    <p style={{ fontWeight: "bold", fontSize: "1.15em", color: "#444", marginBottom: 8 }}>
      Session: {sessionName}
    </p>
  ) : null;
}

// --- CSS for disabling select/copy ---
const noSelectStyle = `
.chart-no-select, .chart-no-select * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -ms-user-select: none !important;
  -moz-user-select: none !important;
}
`;

export default function MedalTallySection() {
  const [overall, setOverall] = useState([]);
  const [athletics, setAthletics] = useState([]);
  const [sessionName, setSessionName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const overallUrl = "/api/home/overall-mt";
  const athleticsUrl = "/api/home/medal-tally";
  const sessionUrl = "/api/home/session-name";

  useEffect(() => {
    Promise.all([
      fetch(overallUrl).then((r) => {
        if (!r.ok) throw new Error("Failed to fetch overall medal tally");
        return r.json();
      }),
      fetch(athleticsUrl).then((r) => {
        if (!r.ok) throw new Error("Failed to fetch athletics medal tally");
        return r.json();
      }),
      fetch(sessionUrl).then((r) => {
        if (!r.ok) throw new Error("Failed to fetch session name");
        return r.text();
      }),
    ])
      .then(([overallData, athleticsData, session]) => {
        setOverall(overallData);
        setAthletics(athleticsData);
        setSessionName(session);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
        setLoading(false);
      });
  }, []);

  // Prevent right-click/context menu on chart areas
  useEffect(() => {
    const handler = (e) => {
      if (e.target.closest('.chart-no-select')) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);

  // Prepare chart data for a section
  function getChartData(rows) {
    return {
      labels: rows.map((row) => row.team),
      datasets: [
        {
          label: "Gold",
          data: rows.map((row) => row.gold),
          backgroundColor: "rgba(255, 215, 0, 0.7)",
          borderColor: "rgba(255, 215, 0, 1)",
          borderWidth: 1,
        },
        {
          label: "Silver",
          data: rows.map((row) => row.silver),
          backgroundColor: "rgba(192,192,192, 0.7)",
          borderColor: "rgba(192,192,192, 1)",
          borderWidth: 1,
        },
        {
          label: "Bronze",
          data: rows.map((row) => row.bronze),
          backgroundColor: "rgba(205, 127, 50, 0.7)",
          borderColor: "rgba(205, 127, 50, 1)",
          borderWidth: 1,
        },
      ],
    };
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Medals" }
      },
      x: {
        title: { display: true, text: "Teams" }
      },
    },
  };

  if (loading)
    return (
      <section id="overview" className="parallax-section">
        <style>{noSelectStyle}</style>
        <div className="container">
          <div className="row text-center">
            <div className="col-md-12 wow fadeInUp" data-wow-delay="0.6s">
              <h3>Loading medal tallies...</h3>
            </div>
          </div>
        </div>
      </section>
    );
  if (error)
    return (
      <section id="overview" className="parallax-section">
        <style>{noSelectStyle}</style>
        <div className="container">
          <div className="row text-center">
            <div className="col-md-12 wow fadeInUp" data-wow-delay="0.6s">
              <div style={{ color: "red" }}>Error: {error}</div>
            </div>
          </div>
        </div>
      </section>
    );

  return (
    <section id="overview" className="parallax-section">
      {/* Inject the no-select style only ONCE at the top */}
      <style>{noSelectStyle}</style>
      <div className="container">
        {/* --- Row 1: Athletics Table & Graph --- */}
        <div className="row text-center" style={{ marginBottom: 40 }}>
          {/* Athletics Table */}
          <div className="col-md-6 col-sm-12 wow fadeInUp" data-wow-delay="0.6s">
            <h3>Athletics</h3>
            <SessionLabel sessionName={sessionName} />
            <div className="table-responsive chart-no-select">
              <table className="table table-bordered table-striped">
                <thead className="thead-dark">
                  <tr>
                    <th>Team</th>
                    <th>Gold</th>
                    <th>Silver</th>
                    <th>Bronze</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {athletics && athletics.length > 0 ? (
                    athletics.map((row, idx) => (
                      <tr key={row.team || idx}>
                        <td>{row.team}</td>
                        <td>{row.gold}</td>
                        <td>{row.silver}</td>
                        <td>{row.bronze}</td>
                        <td>{row.total}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>No athletics data.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Athletics Graph */}
          <div className="col-md-6 col-sm-12 wow fadeInUp" data-wow-delay="0.9s" style={{ minHeight: 440 }}>
            <h3>Athletics - Medal Graph</h3>
            <SessionLabel sessionName={sessionName} />
            <div className="chart-no-select" style={{ width: "100%", height: 400 }}>
              <Bar data={getChartData(athletics)} options={chartOptions} />
            </div>
          </div>
        </div>
        {/* --- Row 2: Overall Table & Graph --- */}
        <div className="row text-center">
          {/* Overall Table */}
          <div className="col-md-6 col-sm-12 wow fadeInUp" data-wow-delay="0.6s">
            <h3>Overall Championship</h3>
            <SessionLabel sessionName={sessionName} />
            <div className="table-responsive chart-no-select">
              <table className="table table-bordered table-striped">
                <thead className="thead-dark">
                  <tr>
                    <th>Team</th>
                    <th>Gold</th>
                    <th>Silver</th>
                    <th>Bronze</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {overall && overall.length > 0 ? (
                    overall.map((row, idx) => (
                      <tr key={row.team || idx}>
                        <td>{row.team}</td>
                        <td>{row.gold}</td>
                        <td>{row.silver}</td>
                        <td>{row.bronze}</td>
                        <td>{row.total}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>No overall data.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* Overall Graph */}
          <div className="col-md-6 col-sm-12 wow fadeInUp" data-wow-delay="0.9s" style={{ minHeight: 440 }}>
            <h3>Overall - Medal Graph</h3>
            <SessionLabel sessionName={sessionName} />
            <div className="chart-no-select" style={{ width: "100%", height: 400 }}>
              <Bar data={getChartData(overall)} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* --- View Results Button --- */}
        <div className="row">
          <div className="col-12 text-center" style={{ marginTop: 30 }}>
            <button
              className="btn btn-primary"
              style={{ fontSize: "1.2em", padding: "12px 40px", backgroundColor: "#222" }}
              onClick={() => navigate("/kshetra-results")}
            >
              View Results
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
